import { useEffect, useState } from "react";

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body: React.ReactNode;
  /** User must type this string exactly before the destructive button enables. */
  challenge: string;
  challengeLabel?: string;
  confirmLabel?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  challenge,
  challengeLabel,
  confirmLabel = "I'm flippin' sure",
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const armed = typed === challenge;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md overflow-hidden rounded-xl border border-rose-500/40 bg-zinc-950 shadow-[0_24px_60px_-12px_rgba(244,63,94,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-brand-orange via-rose-500 to-brand-purple" />
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/40">
              ⚠
            </div>
            <h2 className="text-lg font-semibold text-zinc-50">{title}</h2>
          </div>

          <div className="mt-4 text-sm leading-relaxed text-zinc-300">{body}</div>

          <label className="mt-5 block">
            <span className="text-[11px] uppercase tracking-wider text-zinc-500">
              {challengeLabel ?? `Type ${challenge} to confirm`}
            </span>
            <input
              autoFocus
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={challenge}
              className="mt-2 w-full rounded-md border border-zinc-800 bg-zinc-950/80 px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-700 focus:border-rose-500/60 focus:outline-none focus:ring-1 focus:ring-rose-500/40"
            />
          </label>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              className="rounded-md bg-zinc-800/80 px-3 py-1.5 text-xs font-medium text-zinc-200 ring-1 ring-zinc-700 transition hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!armed || busy}
              className={
                "rounded-md px-3 py-1.5 text-xs font-semibold transition " +
                (armed && !busy
                  ? "bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-[0_4px_14px_-4px_rgba(244,63,94,0.6)] ring-1 ring-rose-500/40 hover:brightness-110"
                  : "cursor-not-allowed bg-zinc-900 text-zinc-600 ring-1 ring-zinc-800")
              }
            >
              {busy ? "Sending…" : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
